
import { Flex } from 'antd';
import Icon from '../icon/Icon';
import styles from './Modal.module.scss';
import trashRedSvg from "./assets/images/trash-red-01.svg";
import alertCircleSvg from "./assets/images/alert-circle.svg";
import Button from '../Button';
import { useState, ReactNode } from 'react';
import { useTranslate } from '../language';

export interface ModalProps{
    title: string
    content: string | ReactNode | JSX.Element
    onCancel: () => void
    onConfirm: () => Promise<void> | void
    confirmLabel?: string
    width?: string | number
}

export interface ChoiceModalProps {
    title: string
    content: string | ReactNode | JSX.Element
    onCancel: () => void
    onChoice1: () => Promise<void> | void
    onChoice2: () => Promise<void> | void
    choice1Label: string
    choice2Label: string
    width?: string | number
}

export const DeleteModal = (props: ModalProps) => {

    const {t} = useTranslate()
    const [loading, setLoading] = useState<boolean>()

    const _confirm = async () => {
        if(loading) return;
        setLoading(true)
        try{
            await props.onConfirm()
        }finally{
            setLoading(false)
        }
        
    }

    return (
        <div className={styles['delete-modal']} style={{"margin": "auto", "width": props.width?? 500}}>
            <Flex vertical align='center' gap={10} className={styles['delete-modal__body']}>
                <div className={styles['delete-modal__icon']}>
                    <Icon src={trashRedSvg} size={24}></Icon>
                </div>
                <Flex vertical gap={4}>
                    <div className={styles['delete-modal__title']}>{props.title}</div>
                    <div className={styles['delete-modal__content']}>{props.content}</div>
                </Flex>
            </Flex>
            <div className={styles['delete-modal__footer']}>
                <Flex gap={12}>
                    <Button.WhiteBg width={"100%"} onClick={props.onCancel}>{t("Cancel")}</Button.WhiteBg>
                    <Button.RedBg width={"100%"} onClick={_confirm} loading={loading}>{t("Delete")}</Button.RedBg>
                </Flex>
            </div>
        </div>
    )

}

export const ConfirmModal = (props: ModalProps) => {

    const {t} = useTranslate()
    const [loading, setLoading] = useState<boolean>()

    const _confirm = async () => {
        if(loading) return;
        setLoading(true)
        try{
            await props.onConfirm()
        }finally{
            setLoading(false)
        }
        
    }

    return (
        <div className={styles['confirm-modal']} style={{"margin": "auto", "width": props.width?? 500}}>
            <Flex vertical align='center' gap={10} className={styles['confirm-modal__body']}>
                <div className={styles['confirm-modal__icon']}>
                    <Icon src={alertCircleSvg} size={24}></Icon>
                </div>
                <Flex vertical gap={4}>
                    <div className={styles['confirm-modal__title']}>{props.title}</div>
                    <div className={styles['confirm-modal__content']}>{props.content}</div>
                </Flex>
            </Flex>
            <div className={styles['confirm-modal__footer']}>
                <Flex gap={12}>
                    <Button.WhiteBg width={"100%"} onClick={props.onCancel}>{t("Cancel")}</Button.WhiteBg>
                    <Button.Primary width={"100%"} onClick={_confirm} loading={loading}>{props.confirmLabel??t("Confirm")}</Button.Primary>
                </Flex>
            </div>
        </div>
    )

}

export const ChoiceModal = (props: ChoiceModalProps) => {
    const {t} = useTranslate()
    const [loading, setLoading] = useState<boolean>(false);
    const [activeChoice, setActiveChoice] = useState<'choice1' | 'choice2' | null>(null);

    const handleChoice = async (choice: 'choice1' | 'choice2') => {
        if (loading) return;
        setLoading(true);
        setActiveChoice(choice);
        try {
            if (choice === 'choice1') {
                await props.onChoice1();
            } else {
                await props.onChoice2();
            }
        } finally {
            setLoading(false);
            setActiveChoice(null);
        }
    };

    return (
        <div className={styles['choice-modal']} style={{"margin": "auto", "width": props.width?? 500}}>
            <Flex vertical align='center' gap={10} className={styles['choice-modal__body']}>
                <div className={styles['choice-modal__icon']}>
                    <Icon src={alertCircleSvg} size={24}></Icon>
                </div>
                <Flex vertical gap={4}>
                    <div className={styles['choice-modal__title']}>{props.title}</div>
                    <div className={styles['choice-modal__content']}>{props.content}</div>
                </Flex>
            </Flex>
            <div className={styles['choice-modal__footer']}>
                <Flex gap={12} vertical={false}>
                    <Button.WhiteBg width={"100%"} onClick={props.onCancel}>{t("Cancel")}</Button.WhiteBg>
                    <Button.Primary width={"100%"} onClick={() => handleChoice('choice1')} loading={loading && activeChoice === 'choice1'}>
                        {props.choice1Label}
                    </Button.Primary>
                    <Button.Primary width={"100%"} onClick={() => handleChoice('choice2')} loading={loading && activeChoice === 'choice2'}>
                        {props.choice2Label}
                    </Button.Primary>
                </Flex>
            </div>
        </div>
    );
}